<template>
  <div :class="showFolderFilter ? 'folders-column' : 'is-hidden'">
    <p class="folders-header has-text-weight-semibold is-size-5">Folders</p>
    <NrFormInput
      class="folders-search"
      placeholder="filter by folder name"
      label="Filter by folder name"
      v-model="folderSearchTerm"
    />
    <div class="documents-folders">
      <NrButton
        class="is-size-7"
        variant="minimal-neutral"
        @click="
          $emit('filter-by-folder', {
            activeId: 'all',
            type: 'all',
            folderName: 'All Documents'
          })
        "
        :class="getActiveFolder('all')"
        aria-label="Filter by all documents"
      >
        <span class="folder-button-text">All Documents</span>
        <span
          v-if="allDocumentsCount !== undefined && allDocumentsCount > 0"
          :class="[
            'folder-doc-count',
            { 'folder-doc-count--active': activeFolder === 'all' }
          ]"
        >
          {{ allDocumentsCount }}
        </span>
      </NrButton>
    </div>
    <div class="documents-folders">
      <NrButton
        class="is-size-7"
        variant="minimal-neutral"
        @click="
          $emit('filter-by-folder', {
            activeId: 'nofolder',
            type: 'nofolder',
            folderName: 'Documents not in folders'
          })
        "
        :class="getActiveFolder('nofolder')"
        aria-label="Filter by documents not in folders"
      >
        <span class="folder-button-text">Documents not in folders</span>
        <span
          v-if="
            noFolderDocumentsCount !== undefined && noFolderDocumentsCount > 0
          "
          :class="[
            'folder-doc-count',
            { 'folder-doc-count--active': activeFolder === 'nofolder' }
          ]"
        >
          {{ noFolderDocumentsCount }}
        </span>
      </NrButton>
    </div>
    <div v-if="!folders.length" class="empty-state">
      <p
        class="conditional"
        v-if="!folders.length && folderSearchTerm.length > 2"
      >
        No folders found with the name "{{ folderSearchTerm }}" on this request
      </p>
    </div>
    <div class="documents-folders-container">
      <div
        class="documents-folders"
        :key="index"
        v-for="(folder, index) in folders"
      >
        <ParentFolderListItem
          :folder="folder"
          :index="index"
          :folder-opened="foldersOpened.includes(index)"
          :active-folder="activeFolder === `${folder.id}`"
          @filter-by-folder="filterByFolder"
          @rename-folder="renameFolder"
          @open-folder="openFolder"
        />
        <div v-show="foldersOpened.includes(index)" class="folder-content">
          <ChildFolderListItem
            class="folder-content-item"
            v-for="(subfolder, index) in folder.folders"
            :key="index"
            :subfolder="subfolder"
            :parent-folder-id="folder.id"
            :active-folder="activeFolder === `${folder.id}${subfolder.id}`"
            @filter-by-folder="filterByFolder"
            @rename-folder="renameFolder"
          />
          <NrButton
            v-if="existingChildFolders(folder) >= folderLimit"
            @click="incrementChildPageNum(folder)"
            variant="minimal-neutral"
            size="small"
            class="show-more"
          >
            show more
          </NrButton>
        </div>
      </div>
      <NrButton
        v-if="folders.length < totalDocumentsFoldersCount"
        @click="incrementPageNum"
        class="show-more"
        size="small"
        variant="minimal-neutral"
      >
        show more
      </NrButton>
    </div>
  </div>
</template>

<script>
import NrButton from '@nextrequestco/components/src/components/NrButton'
import ParentFolderListItem from '../components/ParentFolderListItem.vue'
import ChildFolderListItem from '../components/ChildFolderListItem'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import { mapGetters } from 'vuex'

export default {
  title: 'Execution/DocumentTab',
  components: {
    NrButton,
    NrFormInput,
    ParentFolderListItem,
    ChildFolderListItem
  },
  emits: [
    'update:modelValue',
    'show-more',
    'child-show-more',
    'filter-by-folder',
    'open-folder',
    'rename-folder'
  ],
  props: {
    activeFolder: {
      type: [String, Number],
      default: 'all'
    },
    foldersOpened: {
      type: Array,
      default: () => []
    },
    limit: {
      type: Number
    },
    folders: {
      type: Array
    },
    showFolderFilter: {
      type: Boolean,
      default: true
    },
    // Folder search term.
    modelValue: {
      type: String
    },
    allDocumentsCount: {
      type: Number,
      default: 0
    },
    noFolderDocumentsCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      filteredFolder: '',
      pageNum: 1,
      childPageNum: 1
    }
  },
  computed: {
    ...mapGetters('request_show', [
      'folderLimit',
      'totalDocumentsFoldersCount'
    ]),

    folderSearchTerm: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    }
  },
  watch: {
    folders(value) {
      // Reset page number if query options change
      if (value.length <= this.limit) {
        this.pageNum = 1
      }
    }
  },
  methods: {
    getActiveFolder(id) {
      return this.activeFolder === id
        ? 'folder-button folder-button-active'
        : 'folder-button'
    },
    existingChildFolders(folder) {
      return folder.folders && folder.folders.length ? folder.folders.length : 0
    },
    incrementPageNum() {
      this.pageNum += 1
      this.$emit('show-more', this.pageNum)
    },
    incrementChildPageNum(folder) {
      this.childPageNum += 1
      const params = {
        childPageNum: this.childPageNum,
        parent_folder_id: folder.id
      }
      this.$emit('child-show-more', params)
    },
    filterByFolder(val) {
      this.$emit('filter-by-folder', val)
    },
    openFolder(val) {
      this.$emit('open-folder', val)
    },
    renameFolder(val) {
      this.$emit('rename-folder', val)
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';
.folders-column {
  background: #f4f4f6;
  min-width: 280px;
  max-width: 280px;
  max-height: 2940px;
  padding: 0;
  .empty-state {
    font-size: 12px;
    padding: 8px;
    .conditional {
      padding-top: 18px;
      padding-bottom: 18px;
      color: #6e6e87;
    }
  }
  .folders-header {
    padding: 4px 8px;
  }
  .folders-search {
    padding: 8px;
  }
  .folders-subheader {
    padding: 6px 8px;
  }
  .documents-folders-container {
    overflow-y: auto;
    height: 2784px;
    &::-webkit-scrollbar {
      width: 2px;
    }
    -ms-overflow-style: none; /* Hide in IE since we can't manually adjust the scrollbar width */
    &::-webkit-scrollbar-thumb {
      background-color: darkgrey;
      outline: 1px solid slategrey;
    }
  }
  .documents-folders {
    width: 100%;
    padding: 6px 8px 4px 8px;
    .folder-button {
      padding-right: 4px;
      padding-left: 4px;
      text-decoration: none;
      width: 100%;
      text-align: left;
      display: flex;
      justify-content: space-between;
      align-items: center;
      &-no-edit-name {
        justify-content: normal;
        .folder-row__title {
          max-width: 100%;
          .parent-folder-name {
            max-width: 190px;
          }
        }
      }
      &.folder-button-active {
        background-color: white;
        .folder-row-buttons {
          background-color: white;
        }
        &:hover {
          .folder-row-buttons {
            background-color: #ebebef;
            &:focus-within {
              background-color: #ebebef;
            }
          }
        }
      }
      &:not(&.folder-button-active) {
        &:hover {
          .folder-row-buttons {
            background-color: #ebebef;
          }
        }
      }
      .nr-button__label {
        margin-left: 8px;
      }

      .parent-folder-name,
      .child-folder-name,
      .caret,
      .folder {
        display: inline-block;
        position: relative;
        margin-right: $spacing-4;
      }
      .parent-folder-name {
        vertical-align: bottom;
        max-width: 16.25em;
      }
      .top-level-document {
        top: -10px;
        margin-left: 8px;
        max-width: 185px;
      }

      .folder-row-buttons {
        min-width: auto;
        padding-right: 4px;
        padding-left: 4px;
        display: flex;
        align-items: center;

        &.has-child-folders {
          max-width: 100%;
        }
        &.folder-row__title {
          text-decoration: none;
          text-align: left;
        }
      }

      .folder-row__title {
        flex: 1;
        justify-content: flex-start;
      }

      .folder-row-with-icon {
        max-width: 160px;
      }
      .folder-row-without-icon {
        max-width: 161px;
      }
      .parent-folder-row-with-caret-icon {
        max-width: 155px;
      }
      &:hover {
        background: #ebebef;
      }
    }

    .folder-content {
      .folder-button {
        padding-left: 8px;
        .folder-row-buttons {
          text-decoration: none;
          .child-folder-name {
            top: 0;
            vertical-align: bottom;
          }
        }
      }
    }
    .folder-button-active {
      background: white;
    }
  }

  .show-more {
    margin-left: 6px;
  }

  .caret-button-reset,
  .rename-button-reset {
    border: none;
    padding: 0;
    margin: 0;
    height: 10px;
    background: transparent;
    &:focus {
      outline: none;
    }
  }

  .folder-button-text {
    text-align: left;
    flex: 1;
  }

  .folder-doc-count {
    background-color: #d8d8d8;
    border-radius: 34px;
    height: 18px;
    padding: 0 6px;
    font-weight: 600;
    font-size: 12px;
    color: $color-purple-110;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    margin-left: auto;
    flex-shrink: 0;

    &--active {
      background-color: $color-purple-110;
      color: white;
    }
  }

  .folder-button {
    .folder-doc-count {
      flex-shrink: 0;
      margin-left: auto;
    }
  }

  .folder-button--child-folder {
    .folder-doc-count {
      margin-left: auto;
    }
  }
}
</style>
